/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Vue } from 'vue-property-decorator';
import router from '@/router';
import type { LivenessSDKResultType } from '@hb/liveness';
import type { LivenessLaunchResponseType } from '@hb/liveness/src/api/liveness/types';
import { LivenessProjectEnum, LivenessSceneEnum } from '@/enums/liveness-param';
import { livenessPop } from '@/components/liveness-pop';
import { ubtSendError } from '@/shared/utils/ubt';
import type { AgreementListData } from '@/api/convergeAgreement/types';
import LivenessSignAgreementsPop from './AgreementsPop.vue';
import type { OpenParams, OpenResult, LivenessSignAgreementsPopProps } from './types';
import { LivenessSignAgreementsPopEvents } from './event-names';

/**
 * LivenessSignAgreementsPop 组件的适配器类
 * 将 Vue 组件实例包装成单例模式
 */
class LivenessSignAgreementsPopAdapter {
  private wrapperInstance: Vue;

  private resolve?: () => void;

  private reject?: () => void;

  constructor() {
    // 保存回调引用，用于在 render 函数中使用
    const onUpdateShow = this.handleUpdateShow.bind(this);
    const onAgreementConfirmed = this.handleAgreementConfirmed.bind(this);
    const onClose = this.handleClose.bind(this);
    const onCancel = this.handleCancel.bind(this);

    // 创建包装器 Vue 实例，用于管理响应式状态
    this.wrapperInstance = new Vue({
      router,
      data() {
        return {
          show: false,
          ocrAfterLoan: false,
          hasNameAndIdCard: false,
        };
      },
      render(h) {
        const vm = this as Vue & LivenessSignAgreementsPopProps;
        return h(LivenessSignAgreementsPop, {
          props: {
            show: vm.show,
            ocrAfterLoan: vm.ocrAfterLoan,
            hasNameAndIdCard: vm.hasNameAndIdCard,
          },
          on: {
            [LivenessSignAgreementsPopEvents.UPDATE_SHOW]: onUpdateShow,
            [LivenessSignAgreementsPopEvents.AGREEMENT_CONFIRMED]: onAgreementConfirmed,
            [LivenessSignAgreementsPopEvents.CLOSE]: onClose,
            [LivenessSignAgreementsPopEvents.CANCEL]: onCancel,
          },
        });
      },
    });

    // 挂载到 DOM
    const container = document.createElement('div');
    document.body.appendChild(container);
    this.wrapperInstance.$mount(container);
  }

  /**
   * 处理弹窗显示状态更新
   */
  private handleUpdateShow(value: boolean): void {
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = value;
  }

  /**
   * 处理协议确认事件
   * @param launchTransaction 活体启动事务参数
   */
  // eslint-disable-next-line max-len
  private async handleAgreementConfirmed(
    params: {
      launchTransaction: LivenessLaunchResponseType;
      agreementListData: AgreementListData;
    },
  ): Promise<void> {
    // 协议确认后，关闭弹窗并跳转活体验证
    this.close();

    try {
      // 获取 ocrAfterLoan 参数
      const vm = this.wrapperInstance.$data as LivenessSignAgreementsPopProps;
      const ocrAfterLoan = vm.ocrAfterLoan || false;

      // 调用活体验证，与 trade-key-component 中的调用方式一致
      const livenessOrderId = await livenessPop.getOrderId({
        project: LivenessProjectEnum.ZYQ,
        scene: LivenessSceneEnum.Authority,
        ocrAfterLoan,
        ocrComplete: params.agreementListData?.ocrComplete,
        launchTransaction: params.launchTransaction
          ? (params.launchTransaction as unknown as Record<string, unknown>)
          : undefined,
        skipAgreementCheck: true,
      });

      // 活体验证成功，触发成功事件
      if (livenessOrderId) {
        this.handleLivenessSuccess();
      }
    } catch (error) {
      ubtSendError({
        name: `handleAgreementConfirmed-liveness-error-${(error as { code?: string }).code || 'unknown'}`,
        message: (error as Error).message,
      });
      // 活体验证失败，触发失败事件
      this.handleLivenessFail();
    }
  }

  /**
   * 处理活体认证成功事件
   */
  private handleLivenessSuccess(): void {
    this.close();
    if (this.resolve) {
      this.resolve();
    }
  }

  /**
   * 处理活体认证失败事件
   */
  private handleLivenessFail(error?: Error): void {
    // 失败时不关闭弹窗，让用户重试
    // 如果已经关闭了弹窗（协议确认后关闭），则触发 reject
    const vm = this.wrapperInstance.$data as { show: boolean };
    if (!vm.show && this.reject) {
      this.reject();
    }
  }

  /**
   * 处理关闭事件
   */
  private handleClose(): void {
    this.close();
    if (this.reject) {
      this.reject();
    }
  }

  /**
   * 处理取消事件
   */
  private handleCancel(): void {
    this.close();
    if (this.reject) {
      this.reject();
    }
  }

  /**
   * 打开弹窗
   */
  public open(params: OpenParams): Promise<void> {
    // 使用 Vue 的响应式数据来控制显示
    const vm = this.wrapperInstance.$data as LivenessSignAgreementsPopProps;
    vm.show = true;
    vm.ocrAfterLoan = params.ocrAfterLoan || false;
    vm.hasNameAndIdCard = params.hasNameAndIdCard || false;

    return new Promise<void>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  /**
   * 关闭弹窗
   */
  public close(): void {
    // 使用 Vue 的响应式数据来控制隐藏
    const vm = this.wrapperInstance.$data as { show: boolean };
    vm.show = false;
  }
}

let instance: LivenessSignAgreementsPopAdapter;

/**
 * 单例模式打开弹窗
 */
export const livenessSignAgreementsPop = {
  /**
   * 打开弹窗
   * @param params 打开参数
   * @returns Promise，resolve 时返回结果
   */
  open(params: OpenParams): Promise<void> {
    if (!instance) {
      instance = new LivenessSignAgreementsPopAdapter();
    }

    return instance.open(params);
  },

  /**
   * 关闭弹窗
   */
  close(): void {
    if (instance) {
      instance.close();
    }
  },
};
